import { defineStore } from 'pinia'
import event from '@/plugins/events'
import { useSalesEligibility } from '@/use/salesEligibility'
import { useHpPartnerLogic } from '@/use/partners/hpPartnerLogic'
import { useCustomerStore } from '@/stores/customer'
import dayjs from '@/plugins/dayjs'

const { localStorage, sessionStorage } = window

const itemTemplate = {
  offer_component_id: null,
  offer_configuration_id: null,
  transaction_type: null,
  product_size: null,
  initial_term: null,
  price: null,
  msrp: null,
  renewal_term: null,
  renewal_price: null,
  cart_id: null,
  master_cart_id: null,
  bundle_cart_id: null,
  bundle_product: null,
  bundle_size: null,
  bundle_term: null,
  bundle_promo_code: null,
  bundle_offer_configuration_id: null,
  bundle_price: null,
  promo_code: null
}

export const useSalesStore = defineStore('sales', {
  state: () => ({
    cart: JSON.parse(sessionStorage.getItem('SalesCart') || '{}'),
    maxId: sessionStorage.getItem('SalesCartMaxId') || 0,
    basket: JSON.parse(sessionStorage.getItem('SalesBasket') || null),
    products: JSON.parse(localStorage.getItem('Products') || '[]'),
    bundles: JSON.parse(localStorage.getItem('Bundles') || '[]'),
    hpProducts: JSON.parse(localStorage.getItem('HP_Products') || '[]'),
    hpBundles: JSON.parse(localStorage.getItem('HP_Bundles') || '[]'),
    benefits: JSON.parse(localStorage.getItem('Benefits') || '{}'),
    salesTransition: sessionStorage.getItem('SalesTransition'),
    salesOfferId: sessionStorage.getItem('SalesOfferId'),
    noOfferReason: sessionStorage.getItem('NoOfferReason'),
    personalizedOffers: JSON.parse(sessionStorage.getItem('PersonalizedOffers') || null),
    productUpgrades: JSON.parse(localStorage.getItem('ProductUpgrades') || null),
    downloadLinks: JSON.parse(localStorage.getItem('DownloadLinks') || null),
    promoCode: sessionStorage.getItem('PromoCode'),
    billingTerms: JSON.parse(localStorage.getItem('BillingTerms') || '[]'),
    productsError: false,
    offersError: false
  }),
  getters: {
    getProductsError: ({productsError}) => productsError,
    getOffersError: ({offersError}) => offersError,
    getCart: ({cart}) => cart,
    getCartList: ({cart}) => Object.values(cart),
    getCartCount: ({cart}) => Object.keys(cart).length,
    getBasket: ({basket}) => basket,
    basketExpired: ({basket}) => basket ? dayjs(basket.expires_at).isBefore(dayjs()) : false,
    getBasketStatus: ({basket, basketExpired}) => {
      if (!basket) return null
      if (basketExpired) return 'Expired'
      if (basket.order_id) return 'Ordered'
      if (basket.declined_at) return 'Payment Declined'
      if (basket.opened_at) return 'Viewed'
      return 'Not Opened'
    },
    getBasketToken: ({basket}) => basket?.token,
    getProducts: ({products, hpProducts}) => {
      if (useHpPartnerLogic().isHpCustomer.value) return hpProducts
      return products
    },
    getBundles: ({bundles, hpBundles}) => {
      if (useHpPartnerLogic().isHpCustomer.value) return hpBundles || []
      return bundles || []
    },
    getPersonalizedOffers: ({personalizedOffers}) => personalizedOffers,
    getAllowSale: ({personalizedOffers}) => personalizedOffers?.allow_sale ?? false,
    // Populated by /sales/offers/personalized whenever allow_sale is false (e.g. customer.upsellable
    // is off, or a restricted partner blocks the sale) -- surfaced so agents know why the Sale
    // Options section is hidden instead of guessing.
    getNoSaleReason: ({personalizedOffers}) => personalizedOffers?.reason ?? null,
    getEligibleActions: ({personalizedOffers}) => personalizedOffers?.eligible_actions ?? [],
    getEligibleBundleNames: ({personalizedOffers}) => personalizedOffers?.eligible_bundle_names ?? [],
    getProductUpgrades: ({productUpgrades}) => productUpgrades,
    getStandardPrice: ({cart}) => parseFloat(Object.values(cart).reduce((sum, c) => sum += (parseFloat(c.price) || 0), 0).toFixed(2)),
    getTotalMSRP: ({cart}) => parseFloat(Object.values(cart).reduce((total, c) => total += (parseFloat(c.msrp) || 0), 0).toFixed(2)),
    getBenefits: ({benefits}) => benefits,
    haveBenefits: ({benefits}) => Object.keys(benefits).length,
    getSalesTransition: ({salesTransition}) => salesTransition,
    getSalesOfferId: ({salesOfferId}) => salesOfferId,
    getNoOfferReason: ({noOfferReason}) => noOfferReason,
    getPromoCode: ({promoCode}) => promoCode,
    getBillingTerms: ({billingTerms}) => billingTerms,
    getDownloadLinks: ({downloadLinks}) => downloadLinks,
    getProductDownloadLink: ({downloadLinks}) => (product) => downloadLinks?.[product]
  },
  actions: {
    findProduct(sub) {
      const {getSalesProductCategory} = useSalesEligibility()
      const pc = getSalesProductCategory(sub)
      const match = (txType) => this.getProducts.find(p =>
        p.transaction_type === (sub.transaction_type === 'Bundle' ? 'Purchase' : txType)
        && p.product_category === pc && p.initial_term === sub.initial_term && p.product_size === sub.product_size
      )
      const tx = sub.transaction_type === 'Bundle' ? 'Purchase' : sub.transaction_type
      let item = match(tx)
      if (!item && tx === 'Upgrade+Renew') item = match('Upgrade') || match('Renew')
      if (!item) {
        const msg = `No matching product found for cart entry: ${JSON.stringify(sub)}`
        event.error(msg)
        throw msg
      }
      return {...item, transaction_type: sub.transaction_type}
    },
    async addItem(sub) {
      // console.log('Adding item to cart', sub)
      if (!sub) return
      let item = this.findProduct(sub)
      if (!item) return
      const {transaction_type, last_order_id, order_id, product_id} = sub
      const orderIdForStop = last_order_id ?? order_id
      const stopSub = sub.status != 'Active - Dunning' && (item.stop_subscription || sub.status?.startsWith('Active'))
      const newItem = {...item, transaction_type, stop_subscription: stopSub, stop_subscription_id: stopSub && orderIdForStop ? `${orderIdForStop}.${product_id}` : null}
      if (transaction_type != 'Bundle' && this.getCartCount > 0) {
        const manualBundle = this.findBundle([newItem.offer_configuration_id, ...this.getCartList.map(c => c.offer_configuration_id)])
        if (manualBundle) {
          await new Promise(resolve => {
            event.emit(event.TRIGGER_CONFIRM_DIALOG, {
              message: `The product you have added completes a bundle configuration in the cart.
                        Would you like to switch to a sales bundle and apply discount?`,
              onYes: async () => {
                const {product_category, product_size, initial_term} = this.getCartList[0]
                // console.log({product_category, product_size, initial_term})
                this.removeItem(this.getCartList[0])
                event.emit('refreshSalesPage')
                sub.transaction_type = 'Bundle'
                newItem.transaction_type = 'Bundle'
                sub.bundle_product = product_category
                newItem.bundle_product = product_category
                sub.bundle_size = product_size
                newItem.bundle_size = product_size
                sub.bundle_term = initial_term
                newItem.bundle_term = initial_term
                event.emit(event.TRIGGER_CONFIRM_DIALOG)
                resolve()
              },
              onNo: () => { event.emit(event.TRIGGER_CONFIRM_DIALOG); resolve() }
            })
          })
        }
      }
      const cart_id = ++this.maxId
      newItem.cart_id = cart_id
      // Preserve product-level promo code
      if (item.promo_code || sub.promo_code) {
        newItem.promo_code = item.promo_code || sub.promo_code
        this.setPromoCode(newItem.promo_code)
      }
      const tmp = {}
      tmp[cart_id] = newItem
      if (sub.transaction_type == 'Bundle') {
        item = this.findProduct({
          transaction_type: 'Bundle', product_category: sub.bundle_product,
          product_size: sub.bundle_size, initial_term: sub.bundle_term
        })
        if (!item) return
        item.master_cart_id = cart_id
        const bundle_cart_id = ++this.maxId
        tmp[cart_id].bundle_cart_id = bundle_cart_id
        item.cart_id = bundle_cart_id
        item.transaction_type = transaction_type
        tmp[bundle_cart_id] = item
        sub.bundle_cart_id = bundle_cart_id
        sub.bundle_offer_configuration_id = item.offer_configuration_id
        const activeSub = useCustomerStore().getSubscriptions.find(s => s.product_category == sub.bundle_product && s.status != 'Active - Dunning' && s.status.startsWith('Active'))
        if (activeSub) {
          const bundleOrderIdForStop = activeSub.last_order_id ?? activeSub.order_id
          if (bundleOrderIdForStop) {
            tmp[bundle_cart_id].stop_subscription = true
            tmp[bundle_cart_id].stop_subscription_id = `${bundleOrderIdForStop}.${activeSub.product_id}`
          }
        }
        const bundle = this.findBundle([tmp[cart_id].offer_configuration_id, tmp[bundle_cart_id].offer_configuration_id])
        if (bundle) {
          // console.log('Found Bundle', bundle)
          sub.bundle_promo_code = bundle.promo_code
          sub.bundle_ock = bundle.bundle_name
          tmp[cart_id].promo_code = bundle.promo_code
          tmp[cart_id].bundle_ock = bundle.bundle_name
          tmp[cart_id].bundle_price = bundle.total_before_tax
          tmp[bundle_cart_id].bundle_ock = bundle.bundle_name
          tmp[bundle_cart_id].promo_code = bundle.promo_code
          if (bundle.promo_code) this.setPromoCode(bundle.promo_code)
        } else {
          console.error(`Bundle not found for ${tmp[cart_id].offer_configuration_id}, ${tmp[bundle_cart_id].offer_configuration_id}`)
        }
      }
      this.cart = Object.assign(this.cart, tmp)
      sub = Object.assign(sub, tmp[cart_id])
      this.saveCart()
      return cart_id
    },
    addBundlePackage(bundle) {
      const customerStore = useCustomerStore()
      const resolved = bundle.products.map(bundleProd => {
        const product = this.getProducts.find(p =>
          p.offer_configuration_id === bundleProd.offer_configuration_id &&
          p.transaction_type === 'Purchase'
        )
        if (!product) {
          console.error(`addBundlePackage: product not found for offer_configuration_id ${bundleProd.offer_configuration_id}`)
          return null
        }
        return { bundleProd, product }
      })
      if (resolved.some(r => r === null)) return
      let primaryCartId = null
      resolved.forEach(({ bundleProd, product }, idx) => {
        const activeSub = customerStore.getSubscriptions.find(s =>
          s.product_category === bundleProd.product_category &&
          s.status !== 'Active - Dunning' &&
          s.status?.startsWith('Active')
        )
        const orderIdForStop = activeSub?.last_order_id ?? activeSub?.order_id
        const stopSub = !!(product.stop_subscription || activeSub)
        const cart_id = ++this.maxId
        if (idx === 0) primaryCartId = cart_id
        this.cart[cart_id] = {
          ...product,
          cart_id,
          transaction_type: 'Bundle',
          master_cart_id: idx > 0 ? primaryCartId : null,
          stop_subscription: stopSub,
          stop_subscription_id: stopSub && orderIdForStop ? `${orderIdForStop}.${activeSub.product_id}` : null,
          promo_code: bundle.promo_code,
          bundle_ock: bundle.bundle_name,
          bundle_price: idx === 0 ? bundle.total_before_tax : null,
        }
      })
      if (bundle.promo_code) this.setPromoCode(bundle.promo_code)
      this.saveCart()
    },
    removeBundlePackage(bundle) {
      const items = this.getCartList.filter(item => item.bundle_ock === bundle.bundle_name)
      items.forEach(item => delete this.cart[item.cart_id])
      this.syncPromoCodeFromCart()
      this.saveCart()
    },
    updateItem(item={}) {
      // console.log(`Updating cart item`, item)
      if (!item.transaction_type) return
      if (!item.cart_id) return console.error(`Attempted to update Sales Cart store with an invalid item`, item)
      let offer = this.findProduct(item)
      this.cart[item.cart_id] = Object.assign(this.cart[item.cart_id], offer)
      item = Object.assign(item, this.cart[item.cart_id])
      if (item.bundle_cart_id) {
        let bun = this.findProduct({
          transaction_type: 'Bundle', product_category: item.bundle_product,
          product_size: item.bundle_size, initial_term: item.bundle_term
        })
        this.cart[item.bundle_cart_id] = Object.assign(this.cart[item.bundle_cart_id], bun)
      }
      this.saveCart()
    },
    getDefaultPromoCode(sub) {
      if (!sub?.product_line_category || !sub?.product_category) return null
      const line = this.getSalesProductCategories()[sub.product_line_category] || []
      return line.find(item => item.product_category == sub.product_category)?.promo_code || null
    },
    syncPromoCodeFromCart() {
      const promoItem = this.getCartList.find(item => item.promo_code)
      this.setPromoCode(promoItem?.promo_code)
    },
    removeItem(sub) {
      // console.log(`Removing Sales Item`, sub)
      if (!sub || !sub.cart_id) return
      const defaultPromoCode = this.getDefaultPromoCode(sub)
      delete this.cart[sub.cart_id]
      if (sub.bundle_cart_id) delete this.cart[sub.bundle_cart_id]
      this.syncPromoCodeFromCart()
      this.saveCart()
      Object.keys(itemTemplate).forEach(k => sub[k] = itemTemplate[k])
      sub.promo_code = defaultPromoCode
    },
    saveCart() {
      sessionStorage.setItem('SalesCart', JSON.stringify(this.cart))
      sessionStorage.setItem('SalesCartMaxId', this.maxId)
    },
    findBundle(ids) {
      // console.log(`Finding bundle for configs`, ids)
      const idCount = ids.length
      const bundle = this.getBundles.find(b => {
        if (b.products.length != idCount) return false
        const configs = b.products.map(p => p.offer_configuration_id)
        for (let i = 0; i < idCount; i++) {
          if (!configs.includes(ids[i])) return false
        }
        return true
      })
      // console.log(`Found matching bundle`, bundle)
      return bundle
    },
    setProducts(products) {
      this.products = products
      products ? localStorage.setItem('Products', JSON.stringify(this.products)) : localStorage.removeItem('Products')
    },
    setProductsError(val) {
      this.productsError = val
    },
    setOffersError(val) {
      this.offersError = val
    },
    setBundles(bundles) {
      this.bundles = bundles
      bundles ? localStorage.setItem('Bundles', JSON.stringify(this.bundles)) : localStorage.removeItem('Bundles')
    },
    setHpProducts(products) {
      this.hpProducts = products
      products ? localStorage.setItem('HP_Products', JSON.stringify(this.hpProducts)) : localStorage.removeItem('HP_Products')
    },
    setHpBundles(bundles) {
      this.hpBundles = bundles
      bundles ? localStorage.setItem('HP_Bundles', JSON.stringify(this.hpBundles)) : localStorage.removeItem('HP_Bundles')
    },
    setBenefits(benefits) {
      if (benefits) {
        const final = {}
        benefits.forEach(b => final[b.product_category] = b.benefits)
        this.benefits = Object.assign({}, final)
        localStorage.setItem('Benefits', JSON.stringify(this.benefits))
      } else {
        this.benefits = Object.assign({}, {})
        localStorage.removeItem('Benefits')
      }
    },
    getProductBenefits(product_category) { return this.benefits[product_category] },
    setBasket(basket) {
      if (basket) {
        console.log(`New Basket`, basket)
        this.basket = Object.assign({}, basket)
        sessionStorage.setItem('SalesBasket', JSON.stringify(this.basket))
      } else {
        this.basket = null
        sessionStorage.removeItem('SalesBasket')
      }
    },
    setSalesTransition(val) {
      if (val) {
        this.salesTransition = val
        sessionStorage.setItem('SalesTransition', val)
      } else {
        this.salesTransition = null
        sessionStorage.removeItem('SalesTransition')
      }
    },
    setSalesOfferId(val) {
      if (val) {
        this.salesOfferId = val
        sessionStorage.setItem('SalesOfferId', val)
      } else {
        this.salesOfferId = null
        sessionStorage.removeItem('SalesOfferId')
      }
    },
    setNoOfferReason(val) {
      if (val) {
        this.noOfferReason = val
        sessionStorage.setItem('NoOfferReason', val)
      } else {
        this.noOfferReason = null
        sessionStorage.removeItem('NoOfferReason')
      }
    },
    setPersonalizedOffers(data) {
      if (data) {
        this.personalizedOffers = data
        sessionStorage.setItem('PersonalizedOffers', JSON.stringify(data))
      } else {
        this.personalizedOffers = null
        sessionStorage.removeItem('PersonalizedOffers')
      }
    },
    setProductUpgrades(products) {
      if (products) {
        this.productUpgrades = products
        localStorage.setItem('ProductUpgrades', JSON.stringify(this.productUpgrades))
      } else {
        this.productUpgrades = []
        localStorage.removeItem('ProductUpgrades')
      }
    },
    setPromoCode(code) {
      this.promoCode = code
      code ? sessionStorage.setItem('PromoCode', this.promoCode) : sessionStorage.removeItem('PromoCode')
    },
    getSalesProductCategories() {
      const final = {}
      this.getProducts?.forEach(p => {
        const {product_line_category, product_line, product_category, promo_code, transaction_type} = p
        if (!final[product_line_category]) {
          final[product_line_category] = []
          final[product_line_category].push({product_line_category, product_line, product_category, promo_code})
        } else {
          const existingIndex = final[product_line_category].findIndex(l => l.product_category == product_category)
          if (existingIndex >= 0) {
            // If we find an existing product with same category, prefer Purchase type products or products with promo_code
            const existing = final[product_line_category][existingIndex]
            // Prioritize Purchase transaction_type or keep one with promo_code
            if (transaction_type == 'Purchase' || (!existing.promo_code && promo_code)) {
              final[product_line_category][existingIndex] = {product_line_category, product_line, product_category, promo_code}
            }
          } else {
            final[product_line_category].push({product_line_category, product_line, product_category, promo_code})
          }
        }
      })
      return final
    },
    setBillingTerms(terms) {
      this.billingTerms = terms
      terms ? localStorage.setItem('BillingTerms', JSON.stringify(this.billingTerms)) : localStorage.removeItem('BillingTerms')
    },
    setDownloadLinks(links) {
      if (links) {
        // Transform array into object with product as key and download_link as value
        const linksObject = {}
        links.forEach(link => {
          if (link.product && link.download_link) linksObject[link.product] = link.download_link
        })
        this.downloadLinks = linksObject
        localStorage.setItem('DownloadLinks', JSON.stringify(linksObject))
      } else {
        localStorage.removeItem('DownloadLinks')
      }
    },
    clearProductData() {
      this.setProducts()
      this.setBundles()
      this.setHpProducts()
      this.setHpBundles()
      this.setBenefits()
      this.setBillingTerms()
      this.setDownloadLinks()
      this.productUpgrades = null
      localStorage.removeItem('ProductUpgrades')
      // Legacy keys — remove on next clear to clean up existing sessions
      localStorage.removeItem('ProductNameConverter')
      localStorage.removeItem('ProductAcronymConverter')
      localStorage.removeItem('OverlappingProducts')
    },
    clearSalesOffer() {
      this.setSalesOfferId()
      this.setSalesTransition()
      this.setNoOfferReason()
      this.setPromoCode()
    },
    clearCart() {
      // console.log('Clearing Sales Cart')
      this.cart = Object.assign({}, {})
      this.maxId = 0
      sessionStorage.removeItem('SalesCart')
      sessionStorage.removeItem('SalesCartMaxId')
      this.setPromoCode()
    },
    clearCustomer() {
      this.setBasket()
      this.clearCart()
      this.clearSalesOffer()
      this.setPersonalizedOffers()
      this.offersError = false
    },
    clear() {
      this.clearCustomer()
      this.clearProductData()
    },
    logout() {
      this.clear()
    }
  }
})